import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Document, DocumentType, DocumentsApiService } from '@vendasta/sales-orders';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';

@Component({
  selector: 'automata-create-proposal-panel',
  templateUrl: './create-proposal-panel.component.html',
  styleUrls: ['./create-proposal-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
  ],
})
export class CreateProposalPanelComponent extends CommonActionPanelComponent implements OnInit {
  templates$: Observable<Document[]>;
  templateIdControl: UntypedFormControl = new FormControl('');
  titleControl: UntypedFormControl = new FormControl('', [Validators.required]);
  isLoading = true;

  disableTemplateSelection$ = this.automationService.isNonPartnerAutomation().pipe(
    take(1),
    map((isNonPartnerAutomation) => {
      if (isNonPartnerAutomation) {
        this.templateIdControl.disable();
        return true;
      }
      this.templateIdControl.setValidators(Validators.required);
      return false;
    }),
  );

  constructor(
    protected readonly automationService: AutomationViewService,
    private readonly documentsApiService: DocumentsApiService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.templateIdControl.setValue(jsonData.template_id);
    this.titleControl.setValue(jsonData.title);

    this.templates$ = this.disableTemplateSelection$.pipe(
      switchMap((isTemplateView) => {
        if (isTemplateView) {
          of([]);
        }
        return this.documentsApiService.searchDocuments({
          filters: {
            types: [DocumentType.DOCUMENT_TYPE_PROPOSAL_TEMPLATE],
            organizationId: this.automation.namespace,
          },
          paging: {
            cursor: '',
            pageSize: 100,
          },
        });
      }),
      map((resp) => resp?.documents || []),
      tap(() => (this.isLoading = false)),

      // When templates are loaded or disabled, enable saving.
      tap(() => (this.savingEnabled = true)),
      take(1),
      shareReplay(1),
    );
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.templateIdControl) {
      return jsonData;
    }
    jsonData.template_id = this.templateIdControl.value;
    jsonData.title = this.titleControl.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.templateIdControl, this.titleControl];
  }

  isFormValid(): boolean {
    return !this.templateIdControl.invalid && !this.titleControl.invalid;
  }
}
